// src/curated/dmc/kashmirVerticalTours.js
// Seed DMC trip: Vertical Tours and Travels Kashmir, 5N/6D Srinagar, Gulmarg, Sonamarg, Pahalgam.
// Source: the DMC's "Promotional packages" itinerary (Bronze / Silver / Gold hotel lists).
//
// This is the fallback copy shipped with the app. Once the partner hub is live, the trip is read
// from the backend (intent dmc_trip_get) and the partner edits it from their dashboard.
//
// RATE CARD NUMBERS ARE PLACEHOLDERS. They are net rates in rupees for the DMC to confirm in
// the partner dashboard (My trips → Rates) before the trip is approved for sale.
//
// Shape (per-night model, see dmcEditor.js):
//   day.starts_in   city the day starts in (null on Day 1: comes from the arrival gateway)
//   day.night       city you sleep in that night (null on the last day)
//   day.place       sightseeing key, add-ons attach to it (gulmarg, sonamarg, pahalgam, srinagar)
//   day.anchor      "start" | "end": Day 1 and the last day never move
//   day.meals       ["B", "D"] as per the meal plan

export const KASHMIR_VERTICAL_TOURS = {
    kind: "dmc",
    // Same link as the live trip on the partner hub. The earlier spelling still opens it (aliases),
    // and the page's canonical points here.
    slug: "kashmir-srinagar-gulmarg-sonamarg-pahalgm",
    aliases: ["kashmir-srinagar-gulmarg-sonamarg-pahalgam"],
    status: "published",
    version: 1,
    partner: {
        agent_id: "b2bsales",
        slug: "vertical-tours-kashmir",
        name: "Vertical Tours and Travels Kashmir",
        short_name: "Vertical Tours",
        tagline: "Approved by the Department of Tourism, Govt. of J&K",
        city: "Srinagar",
        address: "1st Floor, Shop No 6, Shahnawaz Complex, Avenue Colony, Nasim Bagh, Srinagar 190006",
        contact_name: "Zubair",
        phone: "+919796759999",
        logo: "",
        public_ref: "",
        response_time: "Usually replies within 15 minutes, 9 am to 9 pm",
    },
    theme: {
        key: "kashmir",
        accent: "#0E7490",
        accent_soft: "#ECFEFF",
        gradient: "linear-gradient(160deg, #0B3954 0%, #0E7490 45%, #A7C4BC 80%, #F1F5F9 100%)",
        motif: "chinar",
    },
    // Cover photos, shown one after another (components/dmc/HeroSlides.jsx). focus = what to keep
    // in view when the photo is cropped to the wide cover.
    cover_images: [
        { src: "/trips/kashmir/dal-lake.jpg", src_small: "/trips/kashmir/dal-lake-800.jpg", webp: "/trips/kashmir/dal-lake.webp", webp_small: "/trips/kashmir/dal-lake-800.webp", caption: "Shikara on Dal Lake, Srinagar", focus: "50% 82%" },
        { src: "/trips/kashmir/sonamarg.jpg", src_small: "/trips/kashmir/sonamarg-800.jpg", webp: "/trips/kashmir/sonamarg.webp", webp_small: "/trips/kashmir/sonamarg-800.webp", caption: "Meadows of Sonamarg", focus: "60% 88%" },
    ],
    region: "Kashmir",
    title: "Kashmir: Srinagar, Gulmarg, Sonamarg and Pahalgam",
    subtitle: "Dal Lake, three valleys and a night by the Lidder river",
    vibe: ["Mountains", "Dal Lake", "Meadows", "Families"],
    best_months: "Mar to Nov, Dec to Feb for snow",
    max_group: 12,
    min_days: 3,
    max_days: 12,
    search_modes: ["Train", "Flight", "Bus"],
    default_origin: ["Delhi", "New Delhi", "Mumbai"],

    meal_plan: { code: "MAP", label: "Breakfast and dinner at the hotel" },
    // Rooms hold 2 adults plus 1 on an extra bed (the DMC quotes on triple sharing)
    room: { base_occupancy: 2, max_occupancy: 3 },
    // RouteChef's markup on the DMC's net rates. Set by admin, not shown to the partner as editable.
    markup_pct: 10,
    gst_pct: 5,

    tiers: [
        {
            key: "bronze", label: "Bronze", blurb: "Clean 3 star hotels close to the boulevard",
            hotels: {
                Srinagar: "Hotel German Residency or Hotel Oriental Mansion, or similar",
                Pahalgam: "Hotel Home 2 or Blue Whale Resort, or similar",
            },
            room_pn: { Srinagar: 2600, Pahalgam: 2800 },
            extra_bed_pn: { Srinagar: 900, Pahalgam: 900 },
        },
        {
            key: "silver", label: "Silver", blurb: "Boutique stays with better rooms and views",
            hotels: {
                Srinagar: "Hotel Arison Boutique or Hotel Leon, or similar",
                Pahalgam: "River View Resort, or similar",
            },
            room_pn: { Srinagar: 4200, Pahalgam: 4500 },
            extra_bed_pn: { Srinagar: 1300, Pahalgam: 1300 },
        },
        {
            key: "gold", label: "Gold", blurb: "The best rooms in each town",
            hotels: {
                Srinagar: "Hotel Arison Luxury, or similar",
                Pahalgam: "White Water Resort, or similar",
            },
            room_pn: { Srinagar: 7200, Pahalgam: 7500 },
            extra_bed_pn: { Srinagar: 2000, Pahalgam: 2000 },
        },
    ],
    default_tier: "silver",
    check_in: "14:00",
    check_out: "12:00",

    // One Srinagar night can be on a Dal Lake houseboat instead of the hotel
    houseboat: {
        city: "Srinagar",
        label: "Dal Lake houseboat",
        blurb: "A night on a carved cedar houseboat, shikara to the shore",
        room_pn: { bronze: 3500, silver: 5200, gold: 8500 },
        extra_bed_pn: { bronze: 1000, silver: 1400, gold: 2200 },
    },

    // Private cab with driver for the whole trip, picked by group size
    cab: {
        label: "Private cab with driver, all days",
        note: "Non AC in the hills. Local unions run the cabs inside Pahalgam, Sonamarg and Gulmarg.",
        vehicles: [
            { key: "sedan", label: "Etios or Dzire", max_pax: 3, per_day: 3000 },
            { key: "suv", label: "Innova", max_pax: 6, per_day: 4500 },
            { key: "tempo", label: "Tempo Traveller", max_pax: 12, per_day: 6500 },
        ],
    },

    // Where the DMC meets you and drops you. The trip's route search runs to the chosen one.
    // Pickup and drop points. The route search always goes to Srinagar (route_search); routes that
    // stop short (Jammu, Katra) set the pickup to where they end, using `match`:
    //   codes: station / airport codes on the last train or flight leg (dest_code, dest_code_iata)
    //   names: regex on the leg's dest_name
    gateways: [
        {
            key: "srinagar", label: "Srinagar airport or railway station", short: "Srinagar", city: "Srinagar",
            search_name: "Srinagar, Jammu and Kashmir",
            // Day 1 is an evening walk and dinner, so reaching by 6:30 pm still works (the 26403 Vande Bharat gets in at 6 pm)
            lat: 34.0837, lng: 74.7973, start_by: "18:30", supplement_per_vehicle: 0,
            match: { codes: ["SXR", "SINA", "BDGM"], names: "srinagar|budgam|badgam" },
            arrive_note: "Our driver meets you at Srinagar airport or the railway station, whichever you arrive at.",
            hint: "Fly in, or the Vande Bharat via Katra",
        },
        {
            key: "jammu", label: "Jammu Tawi station or Jammu airport", short: "Jammu", city: "Jammu",
            search_name: "Jammu, Jammu and Kashmir",
            lat: 32.7065, lng: 74.8806, start_by: "09:00", supplement_per_vehicle: 3500,
            match: { codes: ["JAT", "IXJ", "JAMMU"], names: "jammu" },
            arrive_note: "Our driver meets you at Jammu Tawi station or Jammu airport. It's about 7 hours to Srinagar through the Banihal tunnel, with tea stops on the way.",
            hint: "Most trains from across India",
        },
        {
            key: "katra", label: "Shri Mata Vaishno Devi Katra station", short: "Katra", city: "Katra",
            search_name: "Katra, Jammu and Kashmir",
            lat: 32.9916, lng: 74.9318, start_by: "09:00", supplement_per_vehicle: 3500,
            match: { codes: ["SVDK"], names: "katra|vaishno" },
            arrive_note: "Our driver meets you at Katra station (SVDK). It's about 6 to 7 hours to Srinagar by road.",
            hint: "Trains from Delhi and the north",
        },
    ],
    // The one place routes are searched to and from: results ending in Jammu or Katra come back too
    route_search_gateway: "srinagar",
    // Older saved versions of this page used separate airport and station pickups
    gateway_aliases: { srinagar_air: "srinagar", srinagar_rail: "srinagar", jammu_rail: "jammu" },
    default_gateway_in: "srinagar",
    default_gateway_out: "srinagar",
    // When a route stops short of Srinagar (Jammu, Katra...), these trains cover the last stretch.
    // Shown as a note on the route; we help the traveller book them.
    onward_trains: {
        to: "Srinagar",
        done: { codes: ["SXR", "SINA", "BDGM"], names: ["srinagar", "budgam", "badgam"] },
        assist: "If you'd like the train, we'll assist in booking it once you book the package.",
        trains: [
            { num: "26401", name: "Vande Bharat", from: "Jammu Tawi (JAT)", dep: "06:20", arr: "11:10", via: "Katra, Reasi, Banihal", not_on: [2], back: "26402" },
            { num: "26403", name: "Vande Bharat", from: "Jammu Tawi (JAT)", dep: "13:20", arr: "18:00", via: "Katra, Reasi, Banihal", not_on: [3], back: "26404" },
            { num: "", name: "Katra to Badgam AC Chair Car", from: "Shri Mata Vaishno Devi Katra (SVDK)", via: "Banihal, Qazigund, Anantnag, Awantipora, Srinagar", runs: "6 days a week", check: true },
            { num: "26407", name: "Vande Bharat via Udhampur", from: "Jammu Tawi (JAT)", back: "26408", announced: true },
        ],
    },
    // Driving hours between towns, both directions
    drive_hours: {
        "Srinagar|Jammu": 7, "Pahalgam|Jammu": 6.5, "Srinagar|Pahalgam": 2.5,
        "Srinagar|Gulmarg": 2.5, "Srinagar|Sonamarg": 2.5, "Pahalgam|Gulmarg": 4.5, "Pahalgam|Sonamarg": 4.5,
        "Srinagar|Katra": 7, "Pahalgam|Katra": 6.5,
        "Srinagar|Doodhpathri": 2, "Pahalgam|Doodhpathri": 3.5, "Srinagar|Yusmarg": 2, "Pahalgam|Yusmarg": 3,
    },

    days: [
        {
            id: "kv-arrive", anchor: "start", starts_in: null, night: "Srinagar", place: "srinagar", meals: ["D"],
            title: "Arrive in Kashmir",
            text: "Your driver meets you and takes you to the hotel in Srinagar. Evening walk on the Boulevard along Dal Lake.",
            blocks: [
                { id: "kv-boulevard", type: "STOP", name: "Boulevard Road walk by Dal Lake", time: "18:00" },
            ],
        },
        {
            id: "kv-srinagar", starts_in: "Srinagar", night: "Srinagar", place: "srinagar", meals: ["B", "D"],
            title: "Srinagar: Mughal gardens and Shankaracharya",
            text: "Nishat Bagh, built by Asif Khan in 1633, Shalimar Bagh, laid out by Jahangir for Nur Jahan in 1619, Chashme Shahi and Pari Mahal. Then up to the Shankaracharya temple for the view over the whole city.",
            blocks: [
                { id: "kv-nishat", type: "STOP", name: "Nishat Bagh", time: "09:30" },
                { id: "kv-shalimar", type: "STOP", name: "Shalimar Bagh", time: "11:00" },
                { id: "kv-chashme", type: "STOP", name: "Chashme Shahi and Pari Mahal", time: "13:30" },
                { id: "kv-shankar", type: "STOP", name: "Shankaracharya temple", time: "15:30", note: "About 250 steps up" },
                { id: "kv-shikara", type: "EXPERIENCE", name: "Shikara ride on Dal Lake, 1 hour", time: "17:30", included: true },
            ],
        },
        {
            id: "kv-gulmarg", starts_in: "Srinagar", night: "Srinagar", place: "gulmarg", day_trip: "Gulmarg", meals: ["B", "D"],
            title: "Day trip to Gulmarg",
            text: "About 52 km, 2.5 hours to the meadow of flowers. One of Asia's best ski slopes in winter and one of the highest golf courses in the world. Ride the Gondola if you like, then back to Srinagar.",
            blocks: [
                { id: "kv-gul-meadow", type: "STOP", name: "Gulmarg meadow and St Mary's church", time: "10:30" },
                { id: "kv-gondola", type: "EXPERIENCE", name: "Gulmarg Gondola", time: "11:30", note: "Phase 1 to Kongdoori (3,000 m), Phase 2 to Apharwat (about 4,000 m)", addon: "gondola" },
            ],
        },
        {
            id: "kv-sonamarg", starts_in: "Srinagar", night: "Srinagar", place: "sonamarg", day_trip: "Sonamarg", meals: ["B", "D"],
            title: "Day trip to Sonamarg",
            text: "The road follows the Sindh river through pine and fir to the meadow of gold. Ride a pony up to Thajiwas glacier if you like, then back to Srinagar.",
            blocks: [
                { id: "kv-sindh", type: "STOP", name: "Sindh river valley drive", time: "09:00" },
                { id: "kv-thajiwas", type: "EXPERIENCE", name: "Thajiwas glacier", time: "12:00", note: "Pony ride or a 3 km walk", addon: "pony" },
            ],
        },
        {
            id: "kv-pahalgam", starts_in: "Srinagar", night: "Pahalgam", place: "pahalgam", meals: ["B", "D"],
            title: "Srinagar to Pahalgam",
            text: "About 3 hours, with stops at the saffron fields of Pampore and an apple orchard. Afternoon by the Lidder river or in the market. Aru, Betaab and Chandanwari go by local union cab.",
            blocks: [
                { id: "kv-pampore", type: "STOP", name: "Pampore saffron fields and apple orchards", time: "10:00" },
                { id: "kv-lidder", type: "STOP", name: "Lidder river walk and Pahalgam market", time: "15:00" },
                { id: "kv-aru", type: "EXPERIENCE", name: "Aru, Betaab and Chandanwari", time: "13:00", note: "Local union cab only", addon: "union_cab" },
            ],
        },
        {
            id: "kv-depart", anchor: "end", starts_in: "Pahalgam", night: null, place: null, meals: ["B"],
            title: "Head home",
            text: "Breakfast, check out and drive to your drop point for the journey home.",
            blocks: [],
        },
    ],

    // Days the traveller (or the AI) can add. starts_in / night decide where they fit.
    day_pool: [
        {
            id: "kv-p-free-srinagar", starts_in: "Srinagar", night: "Srinagar", place: "srinagar", meals: ["B", "D"],
            title: "Slow day in Srinagar",
            text: "Old city walk, Jamia Masjid, the floating vegetable market at dawn, or just the houseboat deck.",
            blocks: [{ id: "kv-p-fs-market", type: "STOP", name: "Floating vegetable market on Dal Lake", time: "06:00", note: "Shikara at dawn" }],
        },
        {
            id: "kv-p-doodhpathri", starts_in: "Srinagar", night: "Srinagar", place: "doodhpathri", day_trip: "Doodhpathri", meals: ["B", "D"],
            title: "Day trip to Doodhpathri",
            text: "Quiet meadows and streams, about 2 hours from Srinagar. Far fewer crowds than Gulmarg.",
            blocks: [{ id: "kv-p-dp-meadow", type: "STOP", name: "Doodhpathri meadows", time: "11:00" }],
        },
        {
            id: "kv-p-yusmarg", starts_in: "Srinagar", night: "Srinagar", place: "yusmarg", day_trip: "Yusmarg", meals: ["B", "D"],
            title: "Day trip to Yusmarg",
            text: "Pine forest and the Doodh Ganga river, about 2 hours from Srinagar.",
            blocks: [{ id: "kv-p-ys", type: "STOP", name: "Yusmarg meadow and Nilnag lake", time: "11:00" }],
        },
        {
            id: "kv-p-free-pahalgam", starts_in: "Pahalgam", night: "Pahalgam", place: "pahalgam", meals: ["B", "D"],
            title: "Aru, Betaab and Chandanwari",
            text: "A full day around Pahalgam by local union cab: Aru village, Betaab valley and Chandanwari.",
            blocks: [{ id: "kv-p-fp-aru", type: "EXPERIENCE", name: "Aru, Betaab and Chandanwari", time: "09:30", note: "Local union cab only", addon: "union_cab" }],
        },
        {
            id: "kv-p-gulmarg-again", starts_in: "Srinagar", night: "Srinagar", place: "gulmarg", day_trip: "Gulmarg", meals: ["B", "D"],
            title: "Snow day in Gulmarg",
            text: "A second Gulmarg day for skiing lessons, sledging or the Gondola on a clearer day.",
            blocks: [{ id: "kv-p-gl-snow", type: "EXPERIENCE", name: "Skiing lesson or sledge ride", time: "10:30", note: "Dec to Mar" }],
        },
    ],

    // Paid extras. "prebook" ones the DMC can book for you (added to the price when picked).
    // The rest are paid on the spot; the page shows a rough cost.
    addons: [
        { id: "gondola_p1", group: "gondola", place: "gulmarg", label: "Gondola Phase 1 tickets", unit: "pp", approx: 900, prebook: true, note: "Tickets sell out in season, we book ahead" },
        { id: "gondola_p2", group: "gondola", place: "gulmarg", label: "Gondola Phase 2 tickets", unit: "pp", approx: 1100, prebook: true, note: "Runs when the weather allows" },
        { id: "chain_jeep", group: "snow", place: "gulmarg", label: "Tangmarg to Gulmarg chain jeep", unit: "vehicle", approx: 3000, prebook: false, months: [12, 1, 2, 3], note: "Needed when the road has snow" },
        { id: "pony", group: "pony", place: "sonamarg", label: "Pony to Thajiwas glacier", unit: "pp", approx: 1400, prebook: false, note: "Fixed union rates, pay the pony owner" },
        { id: "union_cab", group: "union_cab", place: "pahalgam", label: "Union cab for Aru, Betaab and Chandanwari", unit: "vehicle", approx: 3200, prebook: true, note: "Private cabs can't go up, only the local union" },
    ],

    // What the month means on the ground
    seasons: [
        { months: [12, 1, 2], tone: "snow", text: "Snow season. Gulmarg is white and great for skiing. The Tangmarg road may need a chain jeep, and Gondola Phase 2 runs only in clear weather." },
        { months: [3, 4], tone: "bloom", text: "Spring. The Tulip Garden in Srinagar usually opens late March for about a month. Some snow left on the high meadows." },
        { months: [5, 6], tone: "peak", text: "Peak season. All valleys open, long days. Book Gondola tickets early." },
        { months: [7, 8], tone: "info", text: "Summer and the Amarnath Yatra season. Pahalgam is busy and the Chandanwari road can have convoy timings." },
        { months: [9, 10, 11], tone: "autumn", text: "Autumn. Chinar trees turn red and gold, clear skies and fewer crowds. Nights get cold from November." },
    ],

    inclusions: [
        "Hotel stays as per the plan, rooms on twin sharing with an extra bed for the third person",
        "Breakfast and dinner every day (MAP), from dinner on Day 1 to breakfast on the last day",
        "Private cab with driver for the whole trip: pickup, drop, all transfers and sightseeing",
        "Parking, tolls and driver allowance",
        "1 hour shikara ride on Dal Lake",
        "Welcome drink at the Srinagar hotel",
        "Getting there and back: book trains, flights or buses right here on this page, or arrange your own",
    ],
    exclusions: [
        "Meals other than breakfast and dinner",
        "Gondola, pony rides, union cabs and the Tangmarg chain jeep, unless you add them",
        "Entry tickets and guide fees at gardens and monuments",
        "Personal expenses like tips, laundry and phone calls",
        "Costs from roadblocks, weather or flight delays beyond our control",
    ],
};

export default KASHMIR_VERTICAL_TOURS;
